import { useEffect, useState } from 'react'
import type { FormEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import Screen from '../components/ui/Screen'
import BottomNav from '../components/ui/BottomNav'
import Panel from '../components/ui/Panel'
import Avatar from '../components/ui/Avatar'
import Input from '../components/ui/Input'
import Toggle from '../components/ui/Toggle'
import Button from '../components/ui/Button'
import PushSettings from '../components/PushSettings'
import { useToast } from '../components/ui/Toast'
import texts from '../texts'
import { attachEmail, clearAuthMarker, deleteMe, getMe, logout, requestCode, updateMe } from '../lib/api'
import type { Me as MeUser } from '../lib/api'
import {
  browserSupportsWebAuthn,
  deletePasskey,
  listPasskeys,
  PASSKEY_FLAG_KEY,
  registerPasskey
} from '../lib/passkeys'
import { getSettings, updateSetting } from '../lib/settings'
import type { Settings } from '../lib/settings'
import { TURNSTILE_SITE_KEY, useTurnstile } from '../lib/useTurnstile'

function Me() {
  const [me, setMe] = useState<MeUser | null>(null)
  const [settings, setSettingsState] = useState<Settings>(getSettings())
  const [hasPasskey, setHasPasskey] = useState(localStorage.getItem(PASSKEY_FLAG_KEY) === '1')
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [saving, setSaving] = useState(false)
  const [confirmingDelete, setConfirmingDelete] = useState(false)
  const [error, setError] = useState('')
  const [attachStep, setAttachStep] = useState<'email' | 'code'>('email')
  const [attachEmailValue, setAttachEmailValue] = useState('')
  const [attachCode, setAttachCode] = useState('')
  const [attachBusy, setAttachBusy] = useState(false)
  const [attachError, setAttachError] = useState('')
  const navigate = useNavigate()
  const { showToast } = useToast()
  const turnstile = useTurnstile(!!me?.isGuest && attachStep === 'email')

  useEffect(() => {
    getMe().then((data) => {
      setMe(data)
      setName(data.name)
      setEmail(data.email)
    })
  }, [])

  const handleSendAttachCode = async (e: FormEvent) => {
    e.preventDefault()
    setAttachError('')
    if (TURNSTILE_SITE_KEY && !turnstile.token) {
      setAttachError(texts.login.turnstileWait)
      return
    }
    setAttachBusy(true)
    try {
      await requestCode(attachEmailValue, turnstile.token)
      setAttachStep('code')
    } catch (err: any) {
      setAttachError(err.response?.data?.message || texts.me.couldNotSendCode)
      turnstile.reset()
    } finally {
      setAttachBusy(false)
    }
  }

  const handleAttachEmail = async (e: FormEvent) => {
    e.preventDefault()
    setAttachError('')
    setAttachBusy(true)
    try {
      await attachEmail(attachEmailValue, attachCode)
      const updated = await getMe()
      setMe(updated)
      setName(updated.name)
      setEmail(updated.email)
      setAttachStep('email')
      setAttachEmailValue('')
      setAttachCode('')
      showToast(texts.me.emailAttached)
    } catch (err: any) {
      setAttachError(err.response?.data?.message || texts.me.couldNotAttachEmail)
    } finally {
      setAttachBusy(false)
    }
  }

  const toggleSetting = (key: keyof Settings) => {
    setSettingsState(updateSetting(key, !settings[key]))
  }

  const handleCreatePasskey = async () => {
    try {
      await registerPasskey()
      setHasPasskey(true)
      showToast(texts.toasts.passkeyCreated)
    } catch {
      setError(texts.errors.couldNotCreatePasskey)
    }
  }

  const handleRemovePasskey = async () => {
    try {
      const passkeys = await listPasskeys()
      await Promise.all(passkeys.map((p) => deletePasskey(p.id)))
      localStorage.removeItem(PASSKEY_FLAG_KEY)
      setHasPasskey(false)
      showToast(texts.toasts.passkeyRemoved)
    } catch {
      setError(texts.errors.couldNotRemovePasskey)
    }
  }

  const handleSaveAccount = async () => {
    setError('')
    setSaving(true)
    try {
      const response = await updateMe(me?.isGuest ? { name } : { name, email })
      setMe(response.user)
      showToast(texts.toasts.accountSaved)
    } catch (err: any) {
      setError(err.response?.data?.message || texts.errors.couldNotUpdateAccount)
    } finally {
      setSaving(false)
    }
  }

  const handleLogout = async () => {
    await logout()
    navigate('/login')
  }

  const handleDeleteAccount = async () => {
    setError('')
    try {
      await deleteMe()
      clearAuthMarker()
      navigate('/login')
    } catch (err: any) {
      setError(err.response?.data?.message || texts.errors.couldNotDeleteAccount)
    }
  }

  return (
    <Screen>
      <div className="flex items-center gap-2.5">
        <Avatar name={me?.name || ''} />
        <div>
          <h1>{me?.name || ''}</h1>
          <span className="text-[0.85em] text-text-2">{me?.email}</span>
        </div>
      </div>

      <div className="grid divide-y divide-text-2/20 rounded-2xl border border-text-2/20 bg-text-2/10 px-3.5">
        <div className="flex items-center justify-between gap-2.5 py-3">
          <div>
            <b>{texts.me.bigText}</b>
            <div className="text-[0.85em] text-text-2">{texts.me.bigTextHint}</div>
          </div>
          <Toggle checked={settings.big} onChange={() => toggleSetting('big')} label={texts.me.bigText} />
        </div>
        <div className="flex items-center justify-between gap-2.5 py-3">
          <div>
            <b>{texts.me.lightTheme}</b>
            <div className="text-[0.85em] text-text-2">{texts.me.lightThemeHint}</div>
          </div>
          <Toggle checked={settings.light} onChange={() => toggleSetting('light')} label={texts.me.lightTheme} />
        </div>
        <div className="flex items-center justify-between gap-2.5 py-3">
          <div>
            <b>{texts.me.sound}</b>
            <div className="text-[0.85em] text-text-2">{texts.me.soundHint}</div>
          </div>
          <Toggle checked={settings.sound} onChange={() => toggleSetting('sound')} label={texts.me.sound} />
        </div>
        <div className="flex items-center justify-between gap-2.5 py-3">
          <div>
            <b>{texts.me.haptic}</b>
            <div className="text-[0.85em] text-text-2">{texts.me.hapticHint}</div>
          </div>
          <Toggle checked={settings.haptic} onChange={() => toggleSetting('haptic')} label={texts.me.haptic} />
        </div>

        {browserSupportsWebAuthn() && (
          <div className="py-3">
            {hasPasskey ? (
              <div className="flex items-center justify-between gap-2.5">
                <span className="text-[0.9em] text-text-2">{texts.me.passkeyCreated}</span>
                <Button variant="ghost" size="sm" onClick={handleRemovePasskey}>
                  {texts.me.removePasskey}
                </Button>
              </div>
            ) : (
              <Button variant="ghost" full onClick={handleCreatePasskey}>
                {texts.me.createPasskey}
              </Button>
            )}
          </div>
        )}
      </div>

      <PushSettings />

      <Link
        to="/regler"
        className="flex w-full items-center gap-2.5 rounded-xl border border-text-2/20 bg-text-2/10 px-3 py-2.5 text-inherit no-underline"
      >
        <Avatar name="P" index={2} />
        <div className="min-w-0 flex-1">
          <b>{texts.me.rulesLink}</b>
        </div>
        <span>→</span>
      </Link>
      <Link
        to="/klubber"
        className="flex w-full items-center gap-2.5 rounded-xl border border-text-2/20 bg-text-2/10 px-3 py-2.5 text-inherit no-underline"
      >
        <Avatar name="K" index={1} />
        <div className="min-w-0 flex-1">
          <b>{texts.me.clubsLink}</b>
        </div>
        <span>→</span>
      </Link>
      {me?.isAdmin && (
        <Link
          to="/admin"
          className="flex w-full items-center gap-2.5 rounded-xl border border-text-2/20 bg-text-2/10 px-3 py-2.5 text-inherit no-underline"
        >
          <Avatar name="A" index={3} />
          <div className="min-w-0 flex-1">
            <b>{texts.me.adminLink}</b>
          </div>
          <span>→</span>
        </Link>
      )}

      <Panel>
        <span className="font-mono text-[0.72rem] uppercase tracking-[0.08em] text-text-2">{texts.me.account}</span>
        <Input placeholder={texts.me.namePlaceholder} value={name} onChange={(e) => setName(e.target.value)} />
        {me?.isGuest ? (
          <div className="grid gap-2 rounded-xl border border-text-2/20 bg-ink/10 p-3">
            <b>{texts.me.guestTitle}</b>
            <p className="text-[0.85em] text-text-2">{texts.me.guestHint}</p>
            {attachStep === 'email' ? (
              <form className="grid gap-2" onSubmit={handleSendAttachCode}>
                <Input
                  type="email"
                  inputMode="email"
                  placeholder={texts.me.emailPlaceholder}
                  value={attachEmailValue}
                  onChange={(e) => setAttachEmailValue(e.target.value)}
                  required
                />
                {TURNSTILE_SITE_KEY && <div ref={turnstile.ref} />}
                {attachError && <p className="text-red">{attachError}</p>}
                <Button type="submit" full disabled={attachBusy}>
                  {attachBusy ? texts.me.sending : texts.me.sendCode}
                </Button>
              </form>
            ) : (
              <form className="grid gap-2" onSubmit={handleAttachEmail}>
                <Input
                  inputMode="numeric"
                  placeholder={texts.me.codePlaceholder}
                  maxLength={6}
                  value={attachCode}
                  onChange={(e) => setAttachCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  required
                />
                {attachError && <p className="text-red">{attachError}</p>}
                <Button type="submit" full disabled={attachBusy || attachCode.length !== 6}>
                  {attachBusy ? texts.me.attaching : texts.me.attachEmailButton}
                </Button>
              </form>
            )}
          </div>
        ) : (
          <Input
            type="email"
            placeholder={texts.me.emailPlaceholder}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        )}
        {error && <p className="text-red">{error}</p>}
        <div className="grid grid-cols-2 gap-2">
          <Button variant="ghost" onClick={handleLogout}>
            {texts.me.logOut}
          </Button>
          <Button onClick={handleSaveAccount} disabled={saving}>
            {texts.me.save}
          </Button>
        </div>

        {confirmingDelete ? (
          <div className="grid gap-2">
            <p className="text-[0.85em] text-red">{texts.me.deleteAccountConfirm}</p>
            <div className="grid grid-cols-2 gap-2">
              <Button variant="ghost" onClick={() => setConfirmingDelete(false)}>
                {texts.me.cancel}
              </Button>
              <Button variant="danger" onClick={handleDeleteAccount}>
                {texts.me.deleteAccountConfirmButton}
              </Button>
            </div>
          </div>
        ) : (
          <Button variant="danger" full onClick={() => setConfirmingDelete(true)}>
            {texts.me.deleteAccount}
          </Button>
        )}
      </Panel>

      <BottomNav />
    </Screen>
  )
}

export default Me
